<template>
  <div class="profile-page">
    <NavBar />
    
    <div class="page-container">
      <div class="page-header">
        <span class="back-link" @click="$router.push('/')">← 返回首页</span>
        <h1 class="page-title">个人中心</h1>
      </div>
      
      <!-- 菜单导航 -->
      <div class="menu-tabs">
        <span 
          v-for="tab in tabs" 
          :key="tab.key" 
          class="tab-item" 
          :class="{ active: currentMenu === tab.key }"
          @click="handleMenuClick(tab.key)"
        >{{ tab.label }}</span>
      </div>
      
      <!-- 会员中心 -->
      <div v-if="currentMenu === 'profile'" class="section">
        <div class="info-row">
          <span class="info-label">用户名</span>
          <span class="info-value">{{ userInfo.username || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">昵称</span>
          <span class="info-value">{{ userInfo.nickname || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">余额</span>
          <span class="info-value">{{ userInfo.money || '0.00' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">积分</span>
          <span class="info-value">{{ userInfo.score || 0 }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">注册时间</span>
          <span class="info-value">{{ formatDate(userInfo.createtime) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">最近登录</span>
          <span class="info-value">{{ formatDate(userInfo.logintime) }}</span>
        </div>
        <div class="btn-group">
          <button class="btn-edit" @click="showEdit = true">修改资料</button>
          <button class="btn-logout" @click="handleLogout">退出登录</button>
        </div>
      </div>
      
      <!-- 修改资料 -->
      <div v-if="showEdit" class="edit-overlay" @click.self="showEdit = false">
        <div class="edit-panel">
          <h3 class="edit-title">修改资料</h3>
          <div class="form-group">
            <label>昵称</label>
            <input type="text" v-model="editForm.nickname" placeholder="请输入昵称" />
          </div>
          <div class="edit-actions">
            <button class="btn-cancel" @click="showEdit = false">取消</button>
            <button class="btn-save" @click="handleSaveProfile">保存</button>
          </div>
        </div>
      </div>
      
      <!-- 我的订单 -->
      <div v-if="currentMenu === 'orders'" class="section">
        <div v-if="orders.length > 0" class="list">
          <div class="list-item" v-for="order in orders" :key="order.id">
            <div class="list-item-header">
              <span class="order-no">{{ order.order_no }}</span>
              <span class="order-status">{{ getOrderStatusText(order.order_status) }}</span>
            </div>
            <div class="list-item-body" v-for="item in order.items" :key="item.shopitem_id">
              <img class="item-img" :src="item.shopitem_cover" :alt="item.shopitem_name" />
              <div class="item-detail">
                <p class="item-name">{{ item.shopitem_name }}</p>
                <p class="item-price">¥{{ item.price }} x {{ item.quantity }}</p>
              </div>
            </div>
            <div class="list-item-footer">
              <span>合计：¥{{ order.total_amount }}</span>
            </div>
          </div>
        </div>
        <p v-else class="empty">暂无订单</p>
        
        <div class="pagination" v-if="orderTotal > orderLimit">
          <span class="page-btn" :class="{ disabled: orderPage <= 1 }" @click="orderPage > 1 && handleOrderPageChange(orderPage - 1)">上一页</span>
          <span class="page-info">{{ orderPage }} / {{ Math.ceil(orderTotal / orderLimit) }}</span>
          <span class="page-btn" :class="{ disabled: orderPage >= Math.ceil(orderTotal / orderLimit) }" @click="orderPage < Math.ceil(orderTotal / orderLimit) && handleOrderPageChange(orderPage + 1)">下一页</span>
        </div>
      </div>
      
      <!-- 我的发布 -->
      <div v-if="currentMenu === 'myposts'" class="section">
        <div v-if="myPosts.length > 0" class="list">
          <div class="list-item" v-for="post in myPosts" :key="post.id">
            <div class="post-row">
              <div class="post-info">
                <h3 class="post-title">{{ post.title }}</h3>
                <p class="post-meta">{{ post.name }} · {{ formatTime(post.createtime) }}</p>
              </div>
              <img v-if="post.images_url && post.images_url.length > 0" class="post-thumb" :src="post.images_url[0]" />
            </div>
          </div>
        </div>
        <p v-else class="empty">暂无发布记录</p>
        
        <div class="pagination" v-if="postTotal > postLimit">
          <span class="page-btn" :class="{ disabled: postPage <= 1 }" @click="postPage > 1 && handlePostPageChange(postPage - 1)">上一页</span>
          <span class="page-info">{{ postPage }} / {{ Math.ceil(postTotal / postLimit) }}</span>
          <span class="page-btn" :class="{ disabled: postPage >= Math.ceil(postTotal / postLimit) }" @click="postPage < Math.ceil(postTotal / postLimit) && handlePostPageChange(postPage + 1)">下一页</span>
        </div>
      </div>
    </div>
    
    <Footer />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import Footer from '@/components/Footer.vue'
import { getUserInfo, userLogout, getMyInfoList, getOrderList } from '@/api/index.js'

export default {
  name: 'ProfilePage',
  components: {
    NavBar,
    Footer
  },
  data() {
    return {
      currentMenu: 'profile',
      tabs: [
        { key: 'profile', label: '账户' },
        { key: 'orders', label: '订单' },
        { key: 'myposts', label: '发布' }
      ],
      userInfo: {},
      showEdit: false,
      editForm: {
        nickname: ''
      },
      orders: [],
      orderPage: 1,
      orderLimit: 10,
      orderTotal: 0,
      myPosts: [],
      postPage: 1,
      postLimit: 10,
      postTotal: 0
    }
  },
  mounted() {
    this.checkLogin()
    this.loadUserInfo()
  },
  methods: {
    checkLogin() {
      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
      }
    },
    async loadUserInfo() {
      const token = localStorage.getItem('token')
      if (!token) return
      
      try {
        const result = await getUserInfo(token)
        if (result.code === 1 && result.data) {
          this.userInfo = result.data.userinfo
          this.editForm.nickname = this.userInfo.nickname || ''
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
        this.$message.error('加载用户信息失败')
      }
    },
    handleMenuClick(key) {
      this.currentMenu = key
      if (key === 'orders') {
        this.loadOrders()
      } else if (key === 'myposts') {
        this.loadMyPosts()
      }
    },
    handleSaveProfile() {
      // TODO: 调用API保存
      this.userInfo.nickname = this.editForm.nickname
      this.showEdit = false
    },
    async handleLogout() {
      if (!confirm('确定要退出登录吗？')) return
      
      const token = localStorage.getItem('token')
      try {
        await userLogout(token)
      } catch (error) {
        console.error('退出登录失败:', error)
      }
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      this.$router.push('/')
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}`
    },
    async loadOrders() {
      try {
        const params = {
          page: this.orderPage,
          limit: this.orderLimit
        }
        
        const result = await getOrderList(params)
        
        if (result.code === 1 && result.data) {
          this.orders = result.data.list || []
          this.orderTotal = result.data.total || 0
        }
      } catch (error) {
        console.error('加载订单失败:', error)
      }
    },
    handleOrderPageChange(page) {
      this.orderPage = page
      this.loadOrders()
    },
    getPayStatusText(status) {
      const statusMap = {
        'unpaid': '未支付',
        'paid': '已支付',
        'refund': '已退款'
      }
      return statusMap[status] || status
    },
    getPayStatusColor(status) {
      const colorMap = {
        'unpaid': 'red',
        'paid': 'green',
        'refund': 'orange'
      }
      return colorMap[status] || 'default'
    },
    getOrderStatusText(status) {
      const statusMap = {
        'pending': '待确认',
        'confirmed': '已确认',
        'shipped': '已发货',
        'completed': '已完成',
        'cancelled': '已取消'
      }
      return statusMap[status] || status
    },
    getOrderStatusColor(status) {
      const colorMap = {
        'pending': 'orange',
        'confirmed': 'blue',
        'shipped': 'cyan',
        'completed': 'green',
        'cancelled': 'gray'
      }
      return colorMap[status] || 'default'
    },
    async loadMyPosts() {
      try {
        const params = {
          page: this.postPage,
          limit: this.postLimit
        }
        
        const result = await getMyInfoList(params)
        
        if (result.code === 1 && result.data) {
          this.myPosts = result.data.list || []
          this.postTotal = result.data.total || 0
        }
      } catch (error) {
        console.error('加载我的发布失败:', error)
      }
    },
    handlePostPageChange(page) {
      this.postPage = page
      this.loadMyPosts()
    }
  }
}
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  background: #fff;
}

.page-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 24px 80px;
}

.page-header {
  margin-bottom: 30px;
}

.back-link {
  font-size: 14px;
  color: #666;
  cursor: pointer;
  display: inline-block;
  margin-bottom: 20px;
  transition: color 0.2s;
}

.back-link:hover {
  color: #111;
}

.page-title {
  font-size: 32px;
  font-weight: 800;
  color: #111;
  margin: 0;
  padding-bottom: 16px;
  border-bottom: 2px solid #111;
}

.menu-tabs {
  display: flex;
  gap: 24px;
  margin-bottom: 30px;
  border-bottom: 1px solid #eee;
  padding-bottom: 16px;
}

.tab-item {
  font-size: 15px;
  color: #666;
  cursor: pointer;
  padding-bottom: 8px;
  transition: all 0.2s;
}

.tab-item:hover {
  color: #111;
}

.tab-item.active {
  color: #111;
  font-weight: 600;
  border-bottom: 2px solid #111;
  margin-bottom: -17px;
}

.section {
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-label {
  width: 100px;
  flex-shrink: 0;
  font-size: 14px;
  color: #999;
}

.info-value {
  flex: 1;
  font-size: 15px;
  color: #111;
}

.btn-group {
  display: flex;
  gap: 12px;
  margin-top: 30px;
}

.btn-edit,
.btn-logout {
  flex: 1;
  padding: 14px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-edit {
  background: #111;
  color: #fff;
  border: 1px solid #111;
}

.btn-edit:hover {
  background: #333;
}

.btn-logout {
  color: #666;
  background: #fff;
  border: 1px solid #ddd;
}

.btn-logout:hover {
  border-color: #111;
  color: #111;
}

.edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-panel {
  background: #fff;
  padding: 30px;
  width: 400px;
  max-width: 90%;
}

.edit-title {
  font-size: 20px;
  font-weight: 700;
  color: #111;
  margin: 0 0 24px 0;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.form-group input {
  width: 100%;
  padding: 12px;
  font-size: 15px;
  border: 1px solid #ddd;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: #111;
}

.edit-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.btn-cancel,
.btn-save {
  flex: 1;
  padding: 12px;
  font-size: 15px;
  cursor: pointer;
}

.btn-cancel {
  background: #fff;
  border: 1px solid #ddd;
  color: #666;
}

.btn-cancel:hover {
  border-color: #111;
  color: #111;
}

.btn-save {
  background: #111;
  color: #fff;
  border: 1px solid #111;
}

.btn-save:hover {
  background: #333;
}

.list {
  display: flex;
  flex-direction: column;
}

.list-item {
  padding: 16px 0;
  border-bottom: 1px solid #eee;
}

.list-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.order-no {
  font-size: 13px;
  color: #999;
}

.order-status {
  font-size: 13px;
  color: #111;
  font-weight: 500;
}

.list-item-body {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.item-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  flex-shrink: 0;
}

.item-detail {
  flex: 1;
}

.item-name {
  font-size: 14px;
  color: #111;
  margin: 0 0 6px 0;
  line-height: 1.4;
}

.item-price {
  font-size: 13px;
  color: #666;
  margin: 0;
}

.list-item-footer {
  text-align: right;
  font-size: 14px;
  color: #111;
  font-weight: 500;
}

.post-row {
  display: flex;
  gap: 16px;
}

.post-info {
  flex: 1;
}

.post-title {
  font-size: 15px;
  font-weight: 600;
  color: #111;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.post-meta {
  font-size: 13px;
  color: #999;
  margin: 0;
}

.post-thumb {
  width: 70px;
  height: 70px;
  object-fit: cover;
  flex-shrink: 0;
}

.empty {
  text-align: center;
  padding: 60px 0;
  color: #999;
  font-size: 15px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.page-btn {
  font-size: 14px;
  color: #111;
  cursor: pointer;
  padding: 8px 14px;
  border: 1px solid #ddd;
  transition: all 0.2s;
}

.page-btn:hover:not(.disabled) {
  background: #111;
  color: #fff;
  border-color: #111;
}

.page-btn.disabled {
  color: #ccc;
  border-color: #eee;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

@media (max-width: 768px) {
  .page-container {
    padding: 24px 16px 60px;
  }
  
  .page-title {
    font-size: 26px;
  }
  
  .menu-tabs {
    gap: 16px;
  }
  
  .info-row {
    flex-direction: column;
    gap: 4px;
  }
  
  .info-label {
    width: auto;
  }
}
</style>
